import { useState, useEffect } from 'react';
import clsx from 'clsx';

import { getGuestRSVP } from 'api/events';
import InputField from 'components/FormFields/InputField';
import { setCookie, getCookie } from 'utils/cookies';
import dayjs from 'dayjs';

const MAX_ATTEMPTS = 3;
const SEARCH_COOKIE_NAME = 'rsvpMaxAttemptsExceeded';

const RSVP = ({ eventInfo, goToOnlineRsvp }) => {
  const [searchValue, setSearchValue] = useState();
  const [searchResults, setSearchResults] = useState([]);
  const [searchedItem, setSearchedItem] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [isPreparingSearch, setIsPreparingSearch] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isClosed, setIsClosed] = useState(false);

  const { id, reply_by: replyBy } = eventInfo;

  const searchGuests = async () => {
    if (!id) return;
    setIsPreparingSearch(true);
    const response = await getGuestRSVP({
      name: searchValue,
      wedding_id: id,
    });
    const { data } = response;
    if (errorMessage) {
      setErrorMessage('');
    }
    if (data.length === 0) {
      setErrorMessage(
        <>
          We weren't able to locate your invitation. Please search again by
          first or last name, or try other names in your party. Contact your
          host with any questions.
        </>);
      setSearchResults([]);
      setSearchedItem();
    } else if (data.length === 1) {
      goToOnlineRsvp(data[0].uuid);
    } else {
      setSearchResults(data);
      setSearchedItem(data[0].uuid);
    }
    setIsPreparingSearch(false);
    setAttempts(attempts + 1);

    if (attempts >= MAX_ATTEMPTS) {
      closeSearch();
      setCookie(SEARCH_COOKIE_NAME, true, 1);
    }
  };

  const onClick = () => {
    if (isClosed) return;
    if (searchValue) {
      searchGuests();
    }
  };

  const closeSearch = () => {
    setIsClosed(true);
    setErrorMessage('');
  };

  useEffect(() => {
    const isExceeded = getCookie(SEARCH_COOKIE_NAME);
    if (isExceeded) {
      setAttempts(MAX_ATTEMPTS);
      closeSearch();
    }
  }, []);

  const renderSearchForm = () => {
    return (
      <div>
        <h1 className="rsvp__title">Kindly RSVP</h1>
        {replyBy && (
          <p className="rsvp__subtitle">
            by {dayjs(replyBy).format('MMMM DD, YYYY')}.
          </p>
        )}
        <div className="rsvp__description">
          {isClosed ? (
            <>
              <p>
                It looks like you’re having trouble locating your guest record.
                &nbsp;For assistance with online RSVP, please contact your host.
              </p>
            </>
          ) : (
            <>
              <p>Please enter your first and/or last name below to RSVP for your guest party.</p>
            </>
          )}
        </div>
        <div className="rsvp__form">
          <InputField
            name="searchGuest"
            placeholder={isClosed ? '' : 'First and/or last name'}
            showLabel={false}
            defaultValue={searchValue}
            disabled={isClosed}
            onChange={(event) => {
              setSearchValue(event.target.value);
              if (errorMessage) {
                setErrorMessage('');
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                onClick();
              }
            }}
          />
          <button
            onClick={onClick}
            disabled={isClosed}
            className={clsx(
              'rsvp__form-button',
              'button',
              isPreparingSearch ? 'button--add loading' : '',
            )}
          >
            Find your invitation
          </button>
        </div>
        <div className="event-error">{errorMessage}</div>
      </div>
    );
  };

  const renderSearchResults = () => {
    return (
      <div className="rsvp__form-group">
        <h1 className="rsvp__title rsvp__title--left">
          Please select your name to continue.
        </h1>
        {searchResults?.map((item) => {
          return (
            <div key={`searched-item-${item.uuid}`}>
              <input
                id={`searched-item-${item.uuid}`}
                name={`searched-item-${item.uuid}`}
                type="radio"
                value={item.uuid}
                checked={searchedItem === item.uuid}
                onChange={() => {
                  setSearchedItem(item.uuid);
                }}
              />
              <label htmlFor={`searched-item-${item.uuid}`}>
                {item.group_name}
              </label>
            </div>
          );
        })}
        <div className="rsvp__button-group">
          <button
            onClick={() => goToOnlineRsvp(searchedItem)}
            className="rsvp__form-button button"
          >
            Continue
          </button>
          <button
            onClick={() => setSearchResults([])}
            className="rsvp__form-button button"
          >
            Search again
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="rsvp">
      {searchResults?.length ? renderSearchResults() : renderSearchForm()}
    </div>
  );
};

export default RSVP;
